import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

// 레벨 체계. BI 시간표(bi_timetable_classes.level_label)를 기준으로 정리했다.
// 사용 교재는 대외 공개하지 않기로 해서, 각 레벨은 학습 영역(areas)만 노출한다.
const LEVELS = [
  {
    name: 'Beginner',
    stage: '1 · 2단계',
    goal: '파닉스로 영어 읽기를 시작합니다',
    detail: '알파벳 소리 규칙을 익혀 처음 보는 단어도 소리 내어 읽고, 짧은 문장을 따라 읽습니다.',
    areas: ['파닉스', '듣기', '기초 단어'],
    color: 'bg-[#E8F1FF]',
  },
  {
    name: 'Dreamer',
    stage: '1 · 2단계',
    goal: '문장을 읽고 직접 써 봅니다',
    detail: '기초 어휘를 쌓으면서 짧은 글을 이해하고, 배운 표현으로 자기 문장을 만들어 봅니다.',
    areas: ['문장 읽기', '문장 쓰기', '듣기'],
    color: 'bg-[#EAF4FF]',
  },
  {
    name: 'Challenger',
    stage: '1 · 2단계',
    goal: '지문을 읽고 문장으로 정리합니다',
    detail: '주제가 있는 지문을 읽고 내용을 파악하며, 문법에 맞는 문장을 스스로 씁니다.',
    areas: ['지문 독해', '문장 쓰기', '어휘'],
    color: 'bg-[#EDF2FF]',
  },
  {
    name: 'Achiever',
    stage: '1 · 2단계',
    goal: '단락 단위로 읽고 씁니다',
    detail: '여러 단락의 글을 읽고 요지를 파악하며, 자기 생각을 한 단락으로 정리해 씁니다.',
    areas: ['단락 독해', '단락 쓰기', '어휘'],
    color: 'bg-[#F0F0FF]',
  },
  {
    name: 'Leader',
    stage: '1 · 2단계',
    goal: '긴 글을 읽고 짜임새 있게 씁니다',
    detail: '길고 복잡한 지문을 독해하고, 서론·본론·결론을 갖춘 글을 구성합니다.',
    areas: ['장문 독해', '문단 구성', '어휘'],
    color: 'bg-[#F5EEFF]',
  },
  {
    name: 'Master',
    stage: '1 · 2단계',
    goal: '내신·수능형 독해와 에세이',
    detail: '고등 수준의 지문을 분석하고, 주제에 대한 자기 주장을 담은 에세이를 완성합니다.',
    areas: ['수능형 독해', '에세이', '고교 어휘'],
    color: 'bg-[#FFF4E5]',
  },
];

// 레벨테스트는 학생의 현재 단계에 따라 방식이 달라진다.
const LEVEL_TESTS = [
  {
    target: 'Beginner 과정',
    title: '파닉스 읽기 테스트',
    detail: '알파벳과 소리 규칙을 알고 있는지, 간단한 단어를 읽을 수 있는지 확인합니다. 영어를 처음 시작하는 학생도 부담 없이 응시할 수 있습니다.',
    items: ['알파벳 · 소리 규칙', '단어 읽기'],
  },
  {
    target: 'Dreamer 과정부터',
    title: '읽기 · 쓰기 · 듣기 테스트',
    detail: '간단한 문장을 읽고 이해하는지, 들은 내용을 파악하는지, 배운 표현으로 문장을 쓸 수 있는지 함께 확인합니다.',
    items: ['문장 읽기', '문장 쓰기', '듣기'],
  },
  {
    target: 'Challenger 과정 이상',
    title: '공인시험 기반 + 작문 · 스피킹',
    detail: '공인 영어 인증시험 자료를 활용해 독해·듣기 수준을 측정하고, 레벨에 맞는 작문과 스피킹 테스트로 표현력까지 확인합니다.',
    items: ['공인시험 자료', '레벨별 작문', '스피킹'],
  },
];

const STEPS = [
  { no: '01', title: '입학 문의', detail: '전화 또는 홈페이지 문의로 상담을 신청합니다.' },
  { no: '02', title: '레벨 테스트', detail: '현재 수준에 맞는 방식으로 테스트를 진행합니다.' },
  { no: '03', title: '결과 상담 · 반 배정', detail: '결과를 함께 보며 알맞은 레벨과 수업 시간을 정합니다.' },
  { no: '04', title: '수업 시작', detail: '배정된 반에서 수업을 시작하고 앱으로 학습을 이어갑니다.' },
];

function Section({ children, className = '' }) {
  return (
    <section className={`px-[20px] desktop:px-[50px] py-[50px] desktop:py-[100px] ${className}`}>
      <div className="max-w-[1480px] mx-auto">{children}</div>
    </section>
  );
}

function SectionTitle({ en, kr }) {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  return (
    <div ref={ref} className={`mb-[32px] desktop:mb-[60px] ${inView ? 'animate-fadeSlideUp' : ''}`}>
      <h2 className="text-primary font-inter font-semibold text-headline-small desktop:text-headline-large">{en}</h2>
      <p className="kr-font mt-[10px] desktop:mt-[16px] text-secondary-text text-body-large desktop:text-title-large leading-relaxed">
        {kr}
      </p>
    </div>
  );
}

function LevelCard({ level, index }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  return (
    <div
      ref={ref}
      className={`${level.color} rounded-[24px] desktop:rounded-[32px] p-[24px] desktop:p-[32px] ${
        inView ? 'animate-fadeSlideUp' : ''
      }`}
      style={{ animationDelay: `${(index % 3) * 120}ms` }}
    >
      <div className="flex items-baseline gap-[10px] flex-wrap">
        <h3 className="text-primary font-inter font-semibold text-title-large desktop:text-headline-small">
          {level.name}
        </h3>
        <span className="kr-font text-secondary-text text-label-medium">{level.stage}</span>
      </div>

      <p className="kr-font mt-[12px] text-primary-text font-semibold text-body-large desktop:text-title-medium">
        {level.goal}
      </p>
      <p className="kr-font mt-[8px] text-secondary-text text-body-medium desktop:text-body-large leading-relaxed">
        {level.detail}
      </p>

      <ul className="mt-[18px] flex flex-wrap gap-[6px]">
        {level.areas.map((b) => (
          <li
            key={b}
            className="kr-font bg-white/70 text-secondary-text text-label-small desktop:text-label-medium px-[10px] py-[5px] rounded-full"
          >
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdmissionPage() {
  const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.3, triggerOnce: true });

  return (
    <div>
      {/* 상단 배너 */}
      <section ref={heroRef} className="relative overflow-hidden bg-primary">
        <div className="absolute top-[12%] left-[7%] w-[220px] h-[220px] rounded-full bg-white/10 animate-float" />
        <div className="absolute bottom-[12%] right-[9%] w-[170px] h-[170px] rounded-full bg-white/10 animate-floatDelay" />
        <div className="absolute top-[48%] right-[26%] w-[110px] h-[110px] rounded-full bg-white/10 animate-floatSlow" />

        <div className="relative max-w-[1480px] mx-auto px-[20px] desktop:px-[50px] py-[70px] desktop:py-[120px] text-primary-background text-center">
          <h1
            className={`font-inter font-bold text-headline-medium desktop:text-display-medium ${
              heroInView ? 'animate-fadeSlideUp' : ''
            }`}
          >
            입학안내
          </h1>
          <p
            className={`kr-font mt-[16px] desktop:mt-[24px] text-title-small desktop:text-headline-small leading-relaxed desktop:leading-[46px] ${
              heroInView ? 'animate-fadeSlideUp' : ''
            }`}
          >
            아나톨레는 모든 학생이 자기 수준에서 시작합니다.
            <br />
            레벨 테스트로 알맞은 단계를 찾아 드립니다.
          </p>
        </div>
      </section>

      {/* 레벨 체계 */}
      <Section className="bg-primary-background">
        <SectionTitle
          en="Curriculum Levels"
          kr="파닉스부터 수능까지, 6단계 커리큘럼으로 단계별로 올라갑니다."
        />

        {/* 레벨 흐름 */}
        <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[8px] mb-[32px] desktop:mb-[50px]">
          {LEVELS.map((l, i) => (
            <span key={l.name} className="flex items-center gap-[10px]">
              <span className="kr-font text-primary font-inter font-semibold text-body-large desktop:text-title-large">
                {l.name}
              </span>
              {i < LEVELS.length - 1 && <span className="text-custom1">›</span>}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3 gap-[16px] desktop:gap-[24px]">
          {LEVELS.map((level, i) => (
            <LevelCard key={level.name} level={level} index={i} />
          ))}
        </div>
      </Section>

      {/* 레벨 테스트 */}
      <Section className="bg-alternate">
        <SectionTitle
          en="Level Test"
          kr="현재 수준에 따라 테스트 방식이 다릅니다. 처음 오는 학생도 부담 없이 응시할 수 있습니다."
        />

        <div className="grid grid-cols-1 desktop:grid-cols-3 gap-[16px] desktop:gap-[24px]">
          {LEVEL_TESTS.map((t) => (
            <div
              key={t.target}
              className="bg-primary-background rounded-[24px] desktop:rounded-[32px] p-[24px] desktop:p-[36px] shadow-lg flex flex-col"
            >
              <span className="kr-font self-start bg-primary/10 text-primary text-label-medium font-semibold px-[12px] py-[6px] rounded-full">
                {t.target}
              </span>
              <h3 className="kr-font mt-[18px] text-primary-text font-semibold text-title-medium desktop:text-headline-small">
                {t.title}
              </h3>
              <p className="kr-font mt-[12px] text-secondary-text text-body-medium desktop:text-body-large leading-relaxed flex-grow">
                {t.detail}
              </p>
              <ul className="mt-[20px] flex flex-wrap gap-[6px]">
                {t.items.map((i) => (
                  <li
                    key={i}
                    className="kr-font border border-custom2 text-secondary-text text-label-small desktop:text-label-medium px-[10px] py-[5px] rounded-full"
                  >
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* 등록 절차 */}
      <Section className="bg-primary-background">
        <SectionTitle en="How to Apply" kr="문의부터 수업 시작까지 네 단계면 됩니다." />

        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4 gap-[16px] desktop:gap-[24px]">
          {STEPS.map((s) => (
            <div
              key={s.no}
              className="border border-custom2 rounded-[24px] desktop:rounded-[32px] p-[24px] desktop:p-[32px] hover:border-primary transition-colors"
            >
              <span className="font-inter font-bold text-primary/40 text-headline-medium desktop:text-headline-large">
                {s.no}
              </span>
              <h3 className="kr-font mt-[10px] text-primary-text font-semibold text-title-medium desktop:text-title-large">
                {s.title}
              </h3>
              <p className="kr-font mt-[8px] text-secondary-text text-body-medium desktop:text-body-large leading-relaxed">
                {s.detail}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="bg-alternate">
        <div className="bg-primary-background rounded-[30px] desktop:rounded-[40px] shadow-lg px-[24px] py-[32px] desktop:px-[60px] desktop:py-[50px] text-center">
          <h2 className="text-primary font-inter font-semibold text-headline-small desktop:text-headline-medium">
            레벨 테스트를 신청하시겠어요?
          </h2>
          <p className="kr-font mt-[14px] desktop:mt-[20px] text-secondary-text text-body-large desktop:text-title-medium leading-relaxed">
            문의를 남겨 주시면 가까운 캠퍼스에서 테스트 일정을 안내해 드립니다.
          </p>
          <div className="mt-[26px] desktop:mt-[36px] flex flex-wrap gap-[12px] justify-center">
            <Link
              to="/inquiry"
              className="px-[40px] py-[15px] bg-primary rounded-lg text-primary-background text-title-small desktop:text-headline-small font-semibold font-inter hover:shadow-md transition-shadow"
            >
              입학문의
            </Link>
            <Link
              to="/campus"
              className="px-[40px] py-[15px] border border-custom2 rounded-lg text-secondary-text text-title-small desktop:text-headline-small font-semibold font-inter hover:border-primary hover:text-primary transition-colors"
            >
              캠퍼스 위치
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
